import { Box, Divider, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IFormSchemaReport, ICustomerFormSchema } from 'src/generated-types/form-schema'
import { IMainComponent } from 'src/generated-types/main-component'
import { getThemeColor, getTranslatedText } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { SingleChecklistForm } from './SingleChecklistForm'
import {
  ChecklistArea,
  ChecklistHeader,
  SaveGroupFooter,
  StyledCategoryCard,
  StyledCategorySection,
} from './CategorySection.styles'
import { CategoryControls } from './CategoryControls'
import {
  buildChecklistsStateFromReports,
  buildReportsFromChecklistsState,
  ChecklistsState,
  hasFilledChecklistValues,
  isChecklistGroupValid,
  isSaveDisabled as getIsSaveDisabled,
} from './CategorySection.action'
import { GridActionButton } from '../common/GridActionButton'

export interface IEquipmentTypeGroup {
  typeId: number
  typeName: string
  equipments: IMainComponent[]
  schemas: ICustomerFormSchema[]
}

export interface ISavedGroup {
  id: string
  equipmentIds: number[]
  reports: IFormSchemaReport[]
}

export interface ICategorySectionProps {
  group: IEquipmentTypeGroup
  savedGroups: ISavedGroup[]
  onSaveGroup: (equipmentIds: number[], reports: IFormSchemaReport[]) => void
  onRemoveGroup: (groupId: string) => void
  customerId: EntityId
  theme: any
}

export const CategorySection: React.FC<ICategorySectionProps> = ({
  group,
  savedGroups,
  onSaveGroup,
  onRemoveGroup,
  customerId,
  theme,
}) => {
  const { t } = useTranslation(['checklist', 'common'])
  const [formKey, setFormKey] = useState(0)
  const [currentSelection, setCurrentSelection] = useState<IMainComponent[]>(() => [...group.equipments])
  const [viewingGroupId, setViewingGroupId] = useState<string | null>(null)
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null)
  const [hasFilledValues, setHasFilledValues] = useState(false)
  const [isGroupValid, setIsGroupValid] = useState(false)

  const savedEquipmentIds = useMemo(() => new Set(savedGroups.flatMap(g => g.equipmentIds)), [savedGroups])
  const translatedTypeName = useMemo(() => {
    const typeNameString = typeof group.typeName === 'string' ? group.typeName : JSON.stringify(group.typeName)
    return getTranslatedText(typeNameString)
  }, [group.typeName])
  const availableEquipments = useMemo(
    () => group.equipments.filter(e => !savedEquipmentIds.has(e.id as number)),
    [group.equipments, savedEquipmentIds],
  )

  useEffect(() => {
    if (!editingGroupId && !viewingGroupId) {
      setCurrentSelection(availableEquipments)
    }
  }, [savedGroups])

  const checklistsStateRef = useRef<ChecklistsState>({})

  const syncStateFromRef = useCallback(() => {
    setHasFilledValues(hasFilledChecklistValues(checklistsStateRef.current))
    setIsGroupValid(isChecklistGroupValid(group.schemas, checklistsStateRef.current))
  }, [group.schemas])

  const resetToAvailable = useCallback(() => {
    setCurrentSelection(availableEquipments)
    checklistsStateRef.current = {}
    syncStateFromRef()
    setFormKey(prev => prev + 1)
  }, [availableEquipments, syncStateFromRef])

  const loadGroupReports = useCallback(
    (savedGroup: ISavedGroup) => {
      checklistsStateRef.current = buildChecklistsStateFromReports(savedGroup.reports, group.schemas)
      syncStateFromRef()
      setFormKey(prev => prev + 1)
    },
    [group.schemas, syncStateFromRef],
  )

  const handleChecklistChange = useCallback(
    (schemaId: number, versionId: number, values: Record<string, any>, isValid: boolean) => {
      checklistsStateRef.current[schemaId] = { versionId, values, isValid }
      syncStateFromRef()
    },
    [syncStateFromRef],
  )

  const handleSave = useCallback(() => {
    if (currentSelection.length === 0) return

    const reports: IFormSchemaReport[] = buildReportsFromChecklistsState(checklistsStateRef.current)

    if (editingGroupId) {
      onRemoveGroup(editingGroupId)
    }

    currentSelection.forEach(equipment => {
      onSaveGroup([equipment.id as number], reports)
    })

    checklistsStateRef.current = {}
    syncStateFromRef()
    setFormKey(prev => prev + 1)
    setEditingGroupId(null)
  }, [currentSelection, onSaveGroup, editingGroupId, onRemoveGroup, syncStateFromRef])

  const handleSelectionChange = useCallback(
    (newValue: IMainComponent[]) => {
      if (editingGroupId || viewingGroupId) return
      setCurrentSelection(newValue)
    },
    [editingGroupId, viewingGroupId],
  )

  const handleViewGroup = useCallback(
    (savedGroup: ISavedGroup) => {
      if (viewingGroupId === savedGroup.id) {
        setViewingGroupId(null)
        resetToAvailable()
        return
      }
      const groupEquipments = group.equipments.filter(e => savedGroup.equipmentIds.includes(e.id as number))
      setCurrentSelection(groupEquipments)
      setViewingGroupId(savedGroup.id)
      setEditingGroupId(null)
      loadGroupReports(savedGroup)
    },
    [group.equipments, viewingGroupId, resetToAvailable, loadGroupReports],
  )

  const handleEditGroup = useCallback(
    (savedGroup: ISavedGroup) => {
      if (editingGroupId === savedGroup.id) {
        setEditingGroupId(null)
        resetToAvailable()
        return
      }
      const groupEquipments = group.equipments.filter(e => savedGroup.equipmentIds.includes(e.id as number))
      setCurrentSelection(groupEquipments)
      setEditingGroupId(savedGroup.id)
      setViewingGroupId(null)
      loadGroupReports(savedGroup)
    },
    [group.equipments, editingGroupId, resetToAvailable, loadGroupReports],
  )

  const handleDeleteGroup = useCallback(
    (groupId: string) => {
      onRemoveGroup(groupId)
      if (editingGroupId === groupId || viewingGroupId === groupId) {
        setEditingGroupId(null)
        setViewingGroupId(null)
        resetToAvailable()
      }
    },
    [onRemoveGroup, editingGroupId, viewingGroupId, resetToAvailable],
  )

  const getButtonText = useCallback(() => {
    if (editingGroupId) return t('update-group', { ns: 'checklist' })
    if (currentSelection.length === availableEquipments.length && availableEquipments.length > 0) {
      return t('save-all', { ns: 'checklist' })
    }
    return t('save-selected', { ns: 'checklist' })
  }, [editingGroupId, currentSelection.length, availableEquipments.length, t])

  const isSaveDisabled = getIsSaveDisabled(currentSelection.length, isGroupValid, hasFilledValues, editingGroupId)

  return (
    <StyledCategorySection>
      <StyledCategoryCard theme={theme}>
        <CategoryControls
          group={group}
          savedGroups={savedGroups}
          currentSelection={currentSelection}
          savedEquipmentIds={savedEquipmentIds}
          editingGroupId={editingGroupId}
          viewingGroupId={viewingGroupId}
          translatedTypeName={translatedTypeName}
          onSelectionChange={handleSelectionChange}
          onViewGroup={handleViewGroup}
          onEditGroup={handleEditGroup}
          onRemoveGroup={handleDeleteGroup}
        />

        {currentSelection.length > 0 && (
          <>
            <ChecklistArea>
              {group.schemas.map((schema, index) => (
                <Box key={`${schema.id}-${formKey}`}>
                  <ChecklistHeader theme={theme}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={500}
                      sx={{ mb: 1, color: getThemeColor(theme, MUIThemeColors.primaryMain) }}
                    >
                      {schema.name}
                    </Typography>
                  </ChecklistHeader>
                  <Box px={2} pb={2}>
                    <SingleChecklistForm
                      key={`${schema.id}-${formKey}`}
                      schema={schema}
                      onChangeValues={(schemaId, versionId, values, isValid) =>
                        handleChecklistChange(schemaId, versionId, values, isValid)
                      }
                      disabledAddDeviation={true}
                      customerId={customerId}
                      savedValues={checklistsStateRef.current[schema.id as number]?.values}
                      readOnly={!!viewingGroupId}
                    />
                  </Box>
                  {index < group.schemas.length - 1 && <Divider />}
                </Box>
              ))}
            </ChecklistArea>

            <SaveGroupFooter theme={theme}>
              <Flex.Row gap={1} alignItems="center">
                {(editingGroupId || viewingGroupId) && (
                  <GridActionButton
                    buttonText={viewingGroupId ? t('close', { ns: 'common' }) : t('cancel', { ns: 'common' })}
                    onClick={() => {
                      setEditingGroupId(null)
                      setViewingGroupId(null)
                      resetToAvailable()
                    }}
                    textColor={getThemeColor(theme, MUIThemeColors.secondaryMain)}
                    sx={{ border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryMain)}` }}
                  />
                )}
                {!viewingGroupId && (
                  <GridActionButton
                    buttonText={getButtonText()}
                    onClick={handleSave}
                    disabled={isSaveDisabled}
                    toolTipTitle={
                      !isGroupValid ? (t('incomplete-checklists-title', { ns: 'checklist' }) as string) : undefined
                    }
                    bgColor={getThemeColor(theme, isSaveDisabled ? MUIThemeColors.white : MUIThemeColors.primaryMain)}
                    textColor={isSaveDisabled ? undefined : getThemeColor(theme, MUIThemeColors.white)}
                  />
                )}
              </Flex.Row>
            </SaveGroupFooter>
          </>
        )}
      </StyledCategoryCard>
    </StyledCategorySection>
  )
}
